import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import axios from 'axios';
import { toast } from 'sonner';
import { VideoCamera, ArrowSquareOut, CalendarBlank, Clock } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VideoConsultationRoom() {
  const { consultationId } = useParams();
  const { user } = useAuth();
  const [consultation, setConsultation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConsultation();
  }, [consultationId]);

  const fetchConsultation = async () => {
    try {
      const { data } = await axios.get(`${API}/video-consultations/${consultationId}`, { withCredentials: true });
      setConsultation(data);
    } catch (e) {
      toast.error('Failed to load consultation');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-secondary">
        <Navbar />
        <div className="flex items-center justify-center min-h-screen pt-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!consultation) return null;

  const scheduledDate = new Date(consultation.scheduled_at);
  const isPatient = user?.role === 'patient';
  const otherParty = isPatient ? consultation.doctor_name : consultation.patient_name;

  return (
    <div className="min-h-screen bg-surface-secondary" data-testid="video-room-page">
      <Navbar />
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="card mb-4">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
              <div className="flex items-center gap-3">
                <VideoCamera size={32} weight="duotone" className="text-accent" />
                <div>
                  <h1 className="font-manrope text-xl font-bold text-primary">Video Consultation</h1>
                  <p className="text-sm text-secondary">with {otherParty}</p>
                </div>
              </div>
              <span className={`status-badge status-${consultation.status === 'scheduled' ? 'under_review' : 'opinion_ready'}`}>
                {consultation.status}
              </span>
            </div>

            <div className="grid sm:grid-cols-3 gap-4 mt-4 pt-4 border-t border-primary/10">
              <div className="flex items-center gap-2">
                <CalendarBlank size={20} weight="duotone" className="text-primary" />
                <div>
                  <p className="text-xs text-secondary">Date</p>
                  <p className="text-sm font-medium text-primary">{scheduledDate.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Clock size={20} weight="duotone" className="text-primary" />
                <div>
                  <p className="text-xs text-secondary">Time (IST)</p>
                  <p className="text-sm font-medium text-primary">{scheduledDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })} · {consultation.duration_minutes} min</p>
                </div>
              </div>
              <div>
                <p className="text-xs text-secondary">Fee</p>
                <p className="text-sm font-medium text-primary">₹{consultation.fee} · {consultation.payment_status}</p>
              </div>
            </div>

            {consultation.reason && (
              <div className="mt-4 pt-4 border-t border-primary/10">
                <p className="text-xs text-secondary mb-1">Reason</p>
                <p className="text-sm text-secondary">{consultation.reason}</p>
              </div>
            )}
          </div>

          {/* Jitsi Embed */}
          <div className="card p-2 sm:p-3">
            <div className="rounded-lg overflow-hidden bg-black" style={{ aspectRatio: '16/9' }}>
              <iframe
                src={`${consultation.meeting_url}#userInfo.displayName="${encodeURIComponent(user?.name || 'Participant')}"`}
                title="Video Consultation"
                allow="camera; microphone; fullscreen; display-capture; autoplay"
                className="w-full h-full border-0"
                data-testid="jitsi-iframe"
              />
            </div>
            <a
              href={consultation.meeting_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 mt-3 text-sm text-primary hover:underline"
              data-testid="open-in-new-tab"
            >
              <ArrowSquareOut size={16} />
              Open in new tab for better experience
            </a>
            <div className="mt-3 text-center">
              <Link
                to={isPatient ? '/patient' : '/doctor'}
                className="text-sm text-secondary hover:text-primary"
              >
                ← Back to Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
